import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format, parseISO } from 'date-fns';

// Apply jspdf-autotable plugin to jsPDF
applyPlugin(jsPDF);

/**
 * PDF Export Utility for Childcare Reports
 * Provides methods to export various report types to PDF and Excel
 */

// Brand constants
const BRAND_PRIMARY = '#2F6DB5';
const LOGO_URL = "/simple-sign-in-logo.png";

/**
 * Format date for PDF export - handles numeric formats like 8302022.0
 */
const formatDateForPDF = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    // Handle numeric values like 4282016.0 or 8302022
    if (typeof dateStr === 'number' || (typeof dateStr === 'string' && /^\d+\.?\d*$/.test(dateStr))) {
      let numStr = String(dateStr).split('.')[0]; // Remove decimal
      
      // Pad to 8 digits if 7 digits (e.g., 4282016 -> 04282016)
      if (numStr.length === 7) {
        numStr = '0' + numStr;
      }
      
      if (numStr.length === 8) {
        const month = numStr.substring(0, 2);
        const day = numStr.substring(2, 4);
        const year = numStr.substring(4, 8);
        return format(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)), 'MMM d, yyyy');
      }
    }
    
    // Handle MM/DD/YYYY format
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      return format(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)), 'MMM d, yyyy');
    }
    
    // Handle YYYY-MM-DD format
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch { 
    return String(dateStr); // Return as string if parsing fails
  }
};

/**
 * Add branded header to PDF document (logo left, center name centered)
 * @param {jsPDF} pdf - The jsPDF document
 * @param {number} startY - Starting Y position (returns new Y position after header)
 * @param {string} centerName - Name of the childcare center
 */
const addBrandedHeader = async (pdf, startY = 10, centerName = '') => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  try {
    // Try to load and add logo
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = LOGO_URL;
    });
    
    // Add logo (left-aligned)
    const logoWidth = 35;
    const logoHeight = 25;
    pdf.addImage(img, 'PNG', 10, startY, logoWidth, logoHeight);
    
    // Add center name (centered, slightly down)
    if (centerName) {
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.text(centerName, pageWidth / 2, startY + logoHeight / 2 + 3, { align: 'center' });
    }
    
    return startY + logoHeight + 8;
  } catch (e) {
    // Fallback: just center name if logo fails
    if (centerName) {
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.text(centerName, pageWidth / 2, startY + 10, { align: 'center' });
    }
    return startY + 15;
  }
};

/**
 * Generate PDF from HTML element
 * @param {HTMLElement} element - The element to convert to PDF
 * @param {string} filename - Output filename (without .pdf)
 * @param {object} options - Additional options
 */
export const exportElementToPDF = async (element, filename, options = {}) => {
  const {
    orientation = 'portrait',
    format = 'a4',
    margin = 10,
    scale = 2
  } = options;

  try {
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - (margin * 2);
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;

    // Add first page
    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
    heightLeft -= (pageHeight - margin * 2);

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - margin * 2);
    }

    pdf.save(`${filename}.pdf`);
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

/**
 * Generate PDF for Incident Report
 */
export const exportIncidentReportPDF = async (reportData, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(225, 29, 72); // Rose-600
  pdf.rect(0, y, pageWidth, 20, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('INCIDENT REPORT', pageWidth / 2, y + 10, { align: 'center' });
  pdf.setFontSize(9);
  pdf.text('Confidential Document', pageWidth / 2, y + 17, { align: 'center' });
  
  y += 25;
  
  // Reset colors
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(11);
  
  const leftMargin = 15;
  const colWidth = (pageWidth - 30) / 2;
  
  // Info Grid
  const addField = (label, value, x, yPos) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(label.toUpperCase(), x, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text(value || 'N/A', x, yPos + 5);
  };
  
  addField('Date of Incident', reportData.date || '', leftMargin, y);
  addField('Time', reportData.time || '', leftMargin + colWidth, y);
  y += 15;
  
  addField('Child Name', reportData.childName || '', leftMargin, y);
  addField('Staff Reporting', reportData.staffName || '', leftMargin + colWidth, y);
  y += 15;
  
  addField('Location', reportData.location || '', leftMargin, y);
  addField('First Aid Applied', reportData.firstAid || '', leftMargin + colWidth, y);
  y += 15;
  
  if (reportData.witnesses) {
    addField('Witnesses', reportData.witnesses, leftMargin, y);
    y += 15;
  }
  
  // Description section
  y += 5;
  pdf.setFillColor(254, 226, 226); // Rose-100
  pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 8, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(136, 19, 55); // Rose-900
  pdf.text('Description of Incident', leftMargin, y);
  y += 10;
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  const descLines = pdf.splitTextToSize(reportData.description || 'No description provided', pageWidth - 30);
  pdf.text(descLines, leftMargin, y);
  y += descLines.length * 5 + 10;
  
  // Resolution section
  pdf.setFillColor(254, 226, 226);
  pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 8, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(136, 19, 55);
  pdf.text('Resolution / Action Taken', leftMargin, y);
  y += 10;
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  const resLines = pdf.splitTextToSize(reportData.resolution || 'No resolution provided', pageWidth - 30);
  pdf.text(resLines, leftMargin, y);
  y += resLines.length * 5 + 15;
  
  // Signature line
  pdf.setDrawColor(0, 0, 0);
  pdf.line(leftMargin, y + 10, leftMargin + 60, y + 10);
  pdf.setFontSize(9);
  pdf.text('Staff Signature', leftMargin, y + 15);
  
  pdf.line(pageWidth - 75, y + 10, pageWidth - 15, y + 10);
  pdf.text('Date', pageWidth - 75, y + 15);
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`incident_report_${reportData.date || 'undated'}.pdf`);
};

/**
 * Generate PDF for Fire Drill Log
 */
export const exportFireDrillPDF = async (drillData, settings = {}, centerName = '', centerAddress = '', centerPhone = '') => {
  const pdf = new jsPDF({ orientation: 'landscape' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const leftMargin = 14;

  // Generic header
  let y = 12;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 56, 116);
  if (centerName) {
    pdf.text(centerName.toUpperCase(), leftMargin, y);
    y += 5;
  }

  pdf.setFontSize(14);
  pdf.text('FIRE DRILL AND FIRE DETECTION LOG', pageWidth / 2, y, { align: 'center' });

  y += 5;
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);
  if (centerAddress || centerPhone) {
    pdf.text([centerAddress, centerPhone].filter(Boolean).join('  |  '), pageWidth / 2, y, { align: 'center' });
    y += 4;
  }

  y += 4;
  pdf.setDrawColor(0, 56, 116);
  pdf.setLineWidth(0.5);
  pdf.line(leftMargin, y, pageWidth - leftMargin, y);

  y += 5;
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(9);
  if (centerName) {
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Facility: ${centerName}`, leftMargin, y);
  }
  if (settings.periodStart && settings.periodEnd) {
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Period Covering: ${settings.periodStart} to ${settings.periodEnd}`, pageWidth / 2, y, { align: 'center' });
  }

  y += 8;

  // ── Section 1: FIRE DRILL (ONCE EVERY 60 DAYS) ──
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 56, 116);
  pdf.text('FIRE DRILL (ONCE EVERY 60 DAYS)', leftMargin, y);
  y += 5;

  // Column definitions (landscape 297mm wide)
  const cols = [
    { label: 'DATE OF DRILL',   x: leftMargin,      w: 25 },
    { label: 'START TIME',      x: leftMargin + 25,  w: 22 },
    { label: 'HYPOTHETICAL LOCATION', x: leftMargin + 47, w: 48 },
    { label: 'EVACUATION TIME', x: leftMargin + 95,  w: 30 },
    { label: 'NUMBER OF CHILDREN', x: leftMargin + 125, w: 25 },
    { label: 'PARTICIPATING FACILITY PERSONS', x: leftMargin + 150, w: pageWidth - leftMargin - 150 - 14 },
  ];

  const rowH = 7;

  // Header row
  pdf.setFillColor(200, 220, 245);
  pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, rowH, 'F');
  pdf.setDrawColor(150, 150, 150);
  pdf.setLineWidth(0.2);
  pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, rowH);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  cols.forEach(col => {
    pdf.text(col.label, col.x + 1, y + 4.5, { maxWidth: col.w - 2 });
  });
  y += rowH;

  // Data rows
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  if (!drillData || drillData.length === 0) {
    pdf.setTextColor(150, 150, 150);
    pdf.text('No fire drill entries recorded.', leftMargin + 2, y + 5);
    y += 10;
  } else {
    drillData.forEach((drill, index) => {
      if (y > 185) { pdf.addPage(); y = 20; }
      // Calculate row height based on staff list length
      const staffText = drill.participatingStaff || '';
      const staffLines = pdf.splitTextToSize(staffText, cols[5].w - 2);
      const rH = Math.max(rowH, staffLines.length * 4 + 3);
      if (index % 2 === 0) {
        pdf.setFillColor(245, 250, 255);
        pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, rH, 'F');
      }
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, rH);
      pdf.setTextColor(0, 0, 0);
      pdf.text(drill.date || '', cols[0].x + 1, y + 4.5);
      pdf.text(drill.time || '', cols[1].x + 1, y + 4.5);
      pdf.text(drill.hypotheticalLocation || '', cols[2].x + 1, y + 4.5, { maxWidth: cols[2].w - 2 });
      pdf.text(`${drill.evacuationMinutes || '0'}m ${drill.evacuationSeconds || '00'}s`, cols[3].x + 1, y + 4.5);
      pdf.text(String(drill.numberOfChildren || ''), cols[4].x + 1, y + 4.5);
      pdf.text(staffLines, cols[5].x + 1, y + 4.5);
      y += rH;
    });
  }

  y += 8;

  // ── Section 2: FIRE DETECTION DEVICES OR SYSTEMS ──
  if (y > 170) { pdf.addPage(); y = 20; }
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 56, 116);
  pdf.text('FIRE DETECTION DEVICES OR SYSTEMS', leftMargin, y);
  y += 4;
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);
  y += 6;

  // Monthly testing row
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const monthColW = (pageWidth - leftMargin * 2) / 12;
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);

  // Label row
  pdf.setFillColor(200, 220, 245);
  pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, 6, 'F');
  pdf.setFontSize(7);
  pdf.text('MANUAL FIRE DETECTION TESTING DATE (Every 30 Days)', leftMargin + 2, y + 4);
  y += 6;

  // Month headers
  pdf.setFillColor(230, 240, 255);
  pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, 6, 'F');
  months.forEach((m, i) => {
    pdf.setDrawColor(180, 180, 180);
    pdf.rect(leftMargin + i * monthColW, y, monthColW, 6);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(m, leftMargin + i * monthColW + monthColW / 2, y + 4, { align: 'center' });
  });
  y += 6;

  // Month date values — white background
  pdf.setFillColor(255, 255, 255);
  pdf.rect(leftMargin, y, pageWidth - leftMargin * 2, 8, 'F');
  const monthKeys = ['month1','month2','month3','month4','month5','month6','month7','month8','month9','month10','month11','month12'];
  months.forEach((m, i) => {
    pdf.setDrawColor(180, 180, 180);
    pdf.rect(leftMargin + i * monthColW, y, monthColW, 8);
    const val = settings.monthlyInspections ? (settings.monthlyInspections[monthKeys[i]] || '') : '';
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(val ? 0 : 180, val ? 0 : 180, val ? 0 : 180);
    pdf.setFontSize(7);
    pdf.text(val || '--', leftMargin + i * monthColW + monthColW / 2, y + 5, { align: 'center' });
  });
  y += 12;

  // Professional testing & purchase date
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('FIRE SAFETY PROFESSIONAL TESTING DATE (Annually):', leftMargin, y);
  pdf.setFont('helvetica', 'normal');
  pdf.text(settings.professionalTestingDate || '___________________', leftMargin + 85, y);
  pdf.setFontSize(7);
  pdf.setTextColor(120, 120, 120);
  pdf.text('(Documentation must be attached)', leftMargin + 130, y);

  y += 7;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(0, 0, 0);
  pdf.text('FIRE DETECTION DEVICE/SYSTEM PURCHASE DATE:', leftMargin, y);
  pdf.setFont('helvetica', 'normal');
  pdf.text(settings.devicePurchaseDate || '___________________', leftMargin + 75, y);

  // Footer
  const pageH = pdf.internal.pageSize.getHeight();
  pdf.setFontSize(7);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()} | CY 864`, pageWidth / 2, pageH - 6, { align: 'center' });

  pdf.save(`fire_drill_detection_log_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Generate PDF for Staff Evaluation
 */
export const exportStaffEvaluationPDF = async (evalData, staffMember, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(217, 119, 6); // Amber-600
  pdf.rect(0, y, pageWidth, 15, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('STAFF ANNUAL EVALUATION', pageWidth / 2, y + 10, { align: 'center' });
  
  y += 20;
  
  pdf.setTextColor(0, 0, 0);
  const leftMargin = 15;
  const colWidth = (pageWidth - 30) / 2;
  
  // Staff Info
  const addField = (label, value, x, yPos) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(label.toUpperCase(), x, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text(value || 'N/A', x, yPos + 5);
  };
  
  addField('Staff Member', staffMember?.name || evalData.staffName || '', leftMargin, y);
  addField('Email', staffMember?.email || evalData.staffEmail || '', leftMargin + colWidth, y);
  y += 15;
  
  addField('Evaluation Period Start', evalData.evaluationPeriodStart || '', leftMargin, y);
  addField('Evaluation Period End', evalData.evaluationPeriodEnd || '', leftMargin + colWidth, y);
  y += 20;
  
  // Performance Ratings
  pdf.setFillColor(254, 243, 199); // Amber-100
  pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 8, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(146, 64, 14); // Amber-800
  pdf.text('Performance Ratings', leftMargin, y);
  y += 10;
  
  const ratingLabels = {
    '1': 'Needs Improvement',
    '2': 'Below Expectations', 
    '3': 'Meets Expectations',
    '4': 'Exceeds Expectations',
    '5': 'Outstanding'
  };
  
  const ratings = [
    { key: 'jobKnowledge', label: 'Job Knowledge' },
    { key: 'qualityOfWork', label: 'Quality of Work' },
    { key: 'reliability', label: 'Reliability & Attendance' },
    { key: 'communication', label: 'Communication Skills' },
    { key: 'teamwork', label: 'Teamwork' },
    { key: 'initiative', label: 'Initiative' },
    { key: 'professionalDevelopment', label: 'Professional Development' },
    { key: 'childInteraction', label: 'Child Interaction' },
    { key: 'parentInteraction', label: 'Parent Interaction' },
    { key: 'safetyCompliance', label: 'Safety Compliance' }
  ];
  
  pdf.setFontSize(10);
  ratings.forEach((rating, index) => {
    const x = index % 2 === 0 ? leftMargin : leftMargin + colWidth;
    const yPos = y + Math.floor(index / 2) * 8;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    const value = evalData[rating.key];
    const ratingNum = value ? parseInt(value) : 0;
    const ratingText = value ? `${ratingNum}/5 - ${ratingLabels[String(ratingNum)] || ''}` : 'Not Rated';
    pdf.text(`${rating.label}: ${ratingText}`, x, yPos);
  });
  
  y += Math.ceil(ratings.length / 2) * 8 + 10;
  
  // Written sections
  const sections = [
    { key: 'strengths', label: 'Strengths' },
    { key: 'areasForImprovement', label: 'Areas for Improvement' },
    { key: 'goalsForNextYear', label: 'Goals for Next Year' },
    { key: 'supervisorComments', label: 'Supervisor Comments' }
  ];
  
  sections.forEach(section => {
    if (y > 250) {
      pdf.addPage();
      y = 20;
    }
    
    pdf.setFillColor(254, 243, 199);
    pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(146, 64, 14);
    pdf.text(section.label, leftMargin, y);
    y += 8;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    const text = evalData[section.key] || 'Not provided';
    const lines = pdf.splitTextToSize(text, pageWidth - 30);
    pdf.text(lines, leftMargin, y);
    y += lines.length * 5 + 10;
  });
  
  // Signatures
  if (y > 240) {
    pdf.addPage();
    y = 20;
  }
  
  y += 10;
  pdf.setDrawColor(0, 0, 0);
  pdf.line(leftMargin, y + 10, leftMargin + 60, y + 10);
  pdf.setFontSize(9);
  pdf.text(`Supervisor: ${evalData.supervisorName || ''}`, leftMargin, y + 15);
  pdf.text(`Date: ${evalData.supervisorSignatureDate || ''}`, leftMargin, y + 20);
  
  pdf.line(pageWidth - 75, y + 10, pageWidth - 15, y + 10);
  pdf.text(`Staff Acknowledged: ${evalData.staffAcknowledged ? 'Yes' : 'No'}`, pageWidth - 75, y + 15);
  pdf.text(`Date: ${evalData.staffAcknowledgeDate || ''}`, pageWidth - 75, y + 20);
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`staff_evaluation_${(staffMember?.name || evalData.staffName || 'unknown').replace(/\s+/g, '_')}.pdf`);
};

/**
 * Generate Monthly Attendance Report PDF
 */
export const exportMonthlyAttendancePDF = async (reportData, dateRange, centerName = '') => {
  const pdf = new jsPDF('landscape');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(217, 119, 6); // Amber-600
  pdf.rect(0, y, pageWidth, 15, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('MONTHLY ATTENDANCE REPORT', pageWidth / 2, y + 10, { align: 'center' });
  
  y += 20;
  
  pdf.setTextColor(0, 0, 0);
  const leftMargin = 10;
  
  // Date range
  pdf.setFontSize(11);
  pdf.text(`Period: ${dateRange.start} to ${dateRange.end}`, pageWidth - 10, y, { align: 'right' });
  y += 10;
  
  // Summary stats - updated labels
  if (reportData.summary) {
    pdf.setFillColor(254, 243, 199);
    pdf.rect(leftMargin - 2, y - 4, pageWidth - 16, 12, 'F');
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Total Operating Days: ${reportData.summary.totalOperatingDays || reportData.summary.totalDays || 0}`, leftMargin, y + 2);
    pdf.text(`Total Children Enrolled: ${reportData.summary.totalEnrolledChildren || reportData.summary.uniqueChildren || 0}`, leftMargin + 70, y + 2);
    pdf.text(`Average Daily Attendance: ${reportData.summary.avgDailyAttendance?.toFixed(1) || 0}`, leftMargin + 160, y + 2);
    y += 18;
  }
  
  // Child attendance table
  if (reportData.childAttendance && reportData.childAttendance.length > 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('Attendance by Child', leftMargin, y);
    y += 8;
    
    // Table header - removed Attendance % column
    pdf.setFillColor(217, 119, 6);
    pdf.setTextColor(255, 255, 255);
    pdf.rect(leftMargin - 2, y - 4, pageWidth - 16, 8, 'F');
    pdf.setFontSize(9);
    pdf.text('Child Name', leftMargin, y);
    pdf.text('Category', leftMargin + 70, y);
    pdf.text('Days Present', leftMargin + 130, y);
    pdf.text('Days Absent', leftMargin + 175, y);
    pdf.text('Avg Hours/Day', leftMargin + 220, y);
    y += 8;
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    
    reportData.childAttendance.forEach((child, index) => {
      if (y > pageHeight - 20) {
        pdf.addPage('landscape');
        y = 20;
      }
      
      if (index % 2 === 0) {
        pdf.setFillColor(255, 251, 235); // Amber-50
        pdf.rect(leftMargin - 2, y - 4, pageWidth - 16, 7, 'F');
      }
      
      pdf.text((child.name || '').substring(0, 30), leftMargin, y);
      pdf.text(child.category || 'N/A', leftMargin + 70, y);
      pdf.text(String(child.daysPresent || 0), leftMargin + 130, y);
      pdf.text(String(child.daysAbsent || 0), leftMargin + 175, y);
      pdf.text(child.avgHoursPerDay?.toFixed(1) || 'N/A', leftMargin + 220, y);
      y += 7;
    });
  }
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
  
  pdf.save(`monthly_attendance_${dateRange.start}_to_${dateRange.end}.pdf`);
};

/**
 * Export Staff Record to PDF
 */
export const exportStaffRecordPDF = async (record, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add branded header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Report title
  pdf.setFontSize(16);
  pdf.setTextColor(47, 109, 181);
  pdf.setFont('helvetica', 'bold');
  pdf.text('STAFF RECORD', pageWidth / 2, y, { align: 'center' });
  y += 10;
  
  pdf.setTextColor(0, 0, 0);
  const leftMargin = 15;
  const colWidth = (pageWidth - 30) / 2;
  
  const addField = (label, value, x, yPos) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(label.toUpperCase(), x, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text(value || 'N/A', x, yPos + 5);
  };
  
  // Basic Info
  addField('Staff Name', record.name, leftMargin, y);
  addField('Email', record.email, leftMargin + colWidth, y);
  y += 15;
  
  addField('Start Date', formatDateForPDF(record.startDate), leftMargin, y);
  addField('Status', record.isActive ? 'Active' : 'Inactive', leftMargin + colWidth, y);
  y += 15;
  
  addField('Last Physical', formatDateForPDF(record.lastPhysicalDate), leftMargin, y);
  addField('Next Physical', formatDateForPDF(record.nextPhysicalDate), leftMargin + colWidth, y);
  y += 20;
  
  // Clearances
  pdf.setFillColor(239, 246, 255); // Light blue
  pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 8, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(47, 109, 181);
  pdf.text('Clearances', leftMargin, y);
  y += 10;
  
  const clearances = [
    { key: 'childAbuseClearance', label: 'Child Abuse Clearance' },
    { key: 'criminalRecordCheck', label: 'Criminal Record Check' },
    { key: 'fbiClearance', label: 'FBI Clearance' },
    { key: 'mandatedReporterTraining', label: 'Mandated Reporter Training' },
    { key: 'healthSafetyBasics', label: 'Health & Safety Basics' },
    { key: 'pediatricFirstAidCPR', label: 'Pediatric First Aid & CPR' }
  ];
  
  pdf.setFontSize(10);
  clearances.forEach(clearance => {
    const data = record[clearance.key] || {};
    // Use ASCII-compatible symbols instead of Unicode
    const status = data.status === 'completed' ? '[Y]' : data.status === 'expired' ? '[X]' : '[ ]';
    const expDate = data.expirationDate ? ` (Exp: ${formatDateForPDF(data.expirationDate)})` : '';
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(`${status} ${clearance.label}${expDate}`, leftMargin, y);
    y += 7;
  });
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`staff_record_${(record.name || 'unknown').replace(/\s+/g, '_')}.pdf`);
};

/**
 * Export data to Excel
 */
export const exportToExcel = (data, filename, sheetName = 'Sheet1') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  // Generate buffer
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}.xlsx`);
};

/**
 * Export attendance records to Excel with formatting
 */
export const exportAttendanceToExcel = (records, dateRange) => {
  // Transform data for Excel
  const excelData = records.map(record => ({
    'Date': record.date,
    'Child Name': record.childName,
    'Category': record.category,
    'Drop-Off Person': record.dropOffPerson || '',
    'Drop-Off Time': record.dropOffTime || '',
    'Pick-Up Person': record.pickUpPerson || '',
    'Pick-Up Time': record.pickUpTime || '',
    'Attendance': record.attendance || 'absent',
    'Notes': record.notes || ''
  }));
  
  exportToExcel(excelData, `attendance_${dateRange.start}_to_${dateRange.end}`, 'Attendance');
};

/**
 * Export Infant/Toddler Daily Report to PDF
 */
export const exportDailyReportPDF = async (report, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const reportType = report.reportType === 'toddler' ? 'Toddler' : 'Infant';
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(236, 72, 153); // Pink-500
  pdf.rect(0, y, pageWidth, 20, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${reportType.toUpperCase()} DAILY REPORT`, pageWidth / 2, y + 8, { align: 'center' });
  pdf.setFontSize(9);
  pdf.text(`${report.childName || 'Child'} - ${report.date || 'Date'}`, pageWidth / 2, y + 16, { align: 'center' });
  
  y += 27;
  const leftMargin = 15;
  
  // Staff info
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(10);
  pdf.text(`Staff: ${report.staffName || 'N/A'}`, leftMargin, y);
  if (report.parentEmail) {
    pdf.text(`Parent Email: ${report.parentEmail}`, pageWidth - 15, y, { align: 'right' });
  }
  y += 10;
  
  // Mood
  if (report.mood) {
    pdf.setFillColor(252, 231, 243); // Pink-100
    pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 10, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(157, 23, 77); // Pink-800
    pdf.text('Mood:', leftMargin, y + 2);
    pdf.setFont('helvetica', 'normal');
    pdf.text(report.mood, leftMargin + 20, y + 2);
    y += 15;
  }
  
  pdf.setTextColor(0, 0, 0);
  
  // Feedings Table
  const feedings = (report.feedings || []).filter(f => f.time || f.amount);
  if (feedings.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [['Time', 'Amount', 'Type/Formula', 'Notes']],
      body: feedings.map(f => [f.time || '', f.amount || '', f.formulaType || '', f.notes || '']),
      theme: 'striped',
      headStyles: { fillColor: [236, 72, 153], textColor: 255 },
      margin: { left: leftMargin, right: 15 },
      styles: { fontSize: 9 },
    });
    y = pdf.lastAutoTable.finalY + 8;
  }
  
  // Diaper Changes Table
  const diapers = (report.diaperChanges || []).filter(d => d.time || d.type);
  if (diapers.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [['Time', 'Type', 'Notes']],
      body: diapers.map(d => [d.time || '', d.type || '', d.notes || '']),
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246], textColor: 255 }, // Purple
      margin: { left: leftMargin, right: 15 },
      styles: { fontSize: 9 },
    });
    y = pdf.lastAutoTable.finalY + 8;
  }
  
  // Sleep Periods Table
  const sleeps = (report.sleepPeriods || []).filter(s => s.startTime || s.endTime);
  if (sleeps.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [['Start Time', 'End Time', 'Notes']],
      body: sleeps.map(s => [s.startTime || '', s.endTime || '', s.notes || '']),
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94], textColor: 255 }, // Green
      margin: { left: leftMargin, right: 15 },
      styles: { fontSize: 9 },
    });
    y = pdf.lastAutoTable.finalY + 8;
  }
  
  // Toilet Training (for toddlers)
  const toilets = (report.toiletTraining || []).filter(t => t.time || t.type);
  if (report.reportType === 'toddler' && toilets.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [['Time', 'Type', 'Notes']],
      body: toilets.map(t => [t.time || '', t.type || '', t.notes || '']),
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255 }, // Blue
      margin: { left: leftMargin, right: 15 },
      styles: { fontSize: 9 },
    });
    y = pdf.lastAutoTable.finalY + 8;
  }
  
  // Additional sections
  const sections = [
    { label: 'Activities', value: report.activities },
    { label: 'Milestones', value: report.milestones },
    { label: 'Supplies Needed', value: report.suppliesNeeded },
    { label: 'Additional Notes', value: report.notes },
  ];
  
  sections.forEach(section => {
    if (section.value) {
      if (y > 250) {
        pdf.addPage();
        y = 20;
      }
      
      pdf.setFillColor(252, 231, 243);
      pdf.rect(leftMargin - 2, y - 4, pageWidth - 26, 8, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(157, 23, 77);
      pdf.text(section.label, leftMargin, y + 1);
      y += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(section.value, pageWidth - 30);
      pdf.text(lines, leftMargin, y);
      y += lines.length * 4 + 8;
    }
  });
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`${reportType}_Report_${(report.childName || 'Child').replace(/\s+/g, '_')}_${report.date || 'undated'}.pdf`);
};

/**
 * Export Crib Card to PDF
 */
export const exportCribCardPDF = async (card, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(59, 130, 246); // Blue-500
  pdf.rect(0, y, pageWidth, 20, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('INFANT CRIB CARD', pageWidth / 2, y + 8, { align: 'center' });
  pdf.setFontSize(11);
  pdf.text(card.childName || 'Child Name', pageWidth / 2, y + 16, { align: 'center' });
  
  y += 27;
  const leftMargin = 15;
  const colWidth = (pageWidth - 30) / 2;
  
  pdf.setTextColor(0, 0, 0);
  
  // Emergency Contact Box
  pdf.setFillColor(254, 226, 226); // Red-100
  pdf.rect(leftMargin - 2, y - 2, pageWidth - 26, 25, 'F');
  pdf.setDrawColor(239, 68, 68); // Red-500
  pdf.rect(leftMargin - 2, y - 2, pageWidth - 26, 25, 'S');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(185, 28, 28); // Red-700
  pdf.text('EMERGENCY CONTACT', leftMargin, y + 5);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(10);
  pdf.text(`Parents: ${card.parentNames || '________________'}`, leftMargin, y + 13);
  pdf.text(`Phone: ${card.emergencyPhone || '________________'}`, leftMargin, y + 20);
  y += 33;
  
  // Two column layout for main info
  const addSection = (label, value, x, yPos, width) => {
    pdf.setFillColor(239, 246, 255); // Blue-50
    pdf.rect(x - 2, yPos - 4, width, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(30, 64, 175); // Blue-800
    pdf.text(label, x, yPos);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(9);
    
    // Handle different value types
    let displayValue = 'N/A';
    if (Array.isArray(value)) {
      // Format array values (feedingSchedule, napSchedule)
      displayValue = value.map(item => {
        if (item.time && item.duration) return `${item.time}: ${item.duration}`;
        if (item.time && item.notes) return `${item.time}${item.notes ? ': ' + item.notes : ''}`;
        if (item.time) return item.time;
        return '';
      }).filter(v => v).join(', ') || 'N/A';
    } else if (value) {
      displayValue = value;
    }
    
    const lines = pdf.splitTextToSize(displayValue, width - 5);
    pdf.text(lines, x, yPos + 7);
    return yPos + 8 + lines.length * 4;
  };
  
  // Left column
  let leftY = y;
  
  // Format feeding schedule
  const feedingTimes = Array.isArray(card.feedingSchedule) 
    ? card.feedingSchedule.filter(f => f.time).map(f => f.time).join(', ')
    : card.feedingSchedule || '';
  leftY = addSection('Feeding Times', feedingTimes, leftMargin, leftY, colWidth - 5);
  leftY = addSection('Formula', `${card.formulaType || 'N/A'} - ${card.formulaOunces || '?'} oz`, leftMargin, leftY + 5, colWidth - 5);
  leftY = addSection('Allergies', card.allergies, leftMargin, leftY + 5, colWidth - 5);
  leftY = addSection('Things Baby Likes', card.likes, leftMargin, leftY + 5, colWidth - 5);
  
  // Right column
  let rightY = y;
  
  // Format nap schedule
  const napInfo = Array.isArray(card.napSchedule)
    ? card.napSchedule.filter(n => n.duration).map(n => `${n.time}: ${n.duration}`).join(', ')
    : card.napSchedule || '';
  rightY = addSection('Nap Schedule', napInfo, leftMargin + colWidth, rightY, colWidth - 5);
  rightY = addSection('Sleep Position', card.sleepPosition || 'Back (recommended)', leftMargin + colWidth, rightY + 5, colWidth - 5);
  rightY = addSection('Sleep Notes', card.sleepNotes, leftMargin + colWidth, rightY + 5, colWidth - 5);
  rightY = addSection('Things Baby Dislikes', card.dislikes, leftMargin + colWidth, rightY + 5, colWidth - 5);
  
  y = Math.max(leftY, rightY) + 10;
  
  // Full width sections
  if (card.soothingTechniques) {
    y = addSection('How to Soothe Baby', card.soothingTechniques, leftMargin, y, pageWidth - 30);
    y += 5;
  }
  if (card.specialInstructions) {
    y = addSection('Special Instructions', card.specialInstructions, leftMargin, y, pageWidth - 30);
  }
  
  // Birthdate if available
  if (card.birthdate) {
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`DOB: ${card.birthdate}`, leftMargin, y + 15);
  }
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`Crib_Card_${(card.childName || 'Child').replace(/\s+/g, '_')}.pdf`);
};

/**
 * Export Transportation Notification to PDF (Formal Letter)
 */
export const exportTransportationPDF = async (notification) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const centerInfo = notification.centerInfo || {};
  const centerName = centerInfo.name || '';
  const pickupDropoff = notification.pickupDropoff || {};
  const signature = notification.signature || {};
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  y += 5;
  const leftMargin = 20;
  
  // Date
  pdf.setFontSize(11);
  pdf.setFont('times', 'normal');
  pdf.text(notification.letterDate || '', leftMargin, y);
  y += 15;
  
  // Salutation
  pdf.setFont('times', 'bold');
  pdf.text('To Whom it May Concern', leftMargin, y);
  y += 12;
  
  // Body paragraph 1 - Center info
  pdf.setFont('times', 'normal');
  let para1 = `${centerName} located at ${centerInfo.address || '_____________'} operates a childcare center`;
  if (centerInfo.capacity) para1 += ` with a capacity of ${centerInfo.capacity}`;
  para1 += '.';
  
  if (centerInfo.primaryContact) {
    para1 += ` Primary contact for this location is ${centerInfo.primaryContact}`;
    if (centerInfo.primaryPhone) para1 += ` Phone Number ${centerInfo.primaryPhone}`;
    if (centerInfo.primaryCell) para1 += ` Cell Phone Number ${centerInfo.primaryCell}`;
    if (centerInfo.secondaryContact) para1 += ` and ${centerInfo.secondaryContact}`;
    if (centerInfo.secondaryCell) para1 += ` Cell Phone ${centerInfo.secondaryCell}`;
    para1 += '.';
  }
  
  const para1Lines = pdf.splitTextToSize(para1, pageWidth - 40);
  pdf.text(para1Lines, leftMargin, y);
  y += para1Lines.length * 6 + 8;
  
  // Excursion locations
  const excursions = (notification.excursionLocations || []).filter(l => l.name);
  if (excursions.length > 0) {
    pdf.text('This notice is also to inform all local authorities of our excursion walks to:', leftMargin, y);
    y += 8;
    
    excursions.forEach(loc => {
      pdf.text(`• ${loc.name}${loc.address ? ` - ${loc.address}` : ''}`, leftMargin + 5, y);
      y += 6;
    });
    y += 5;
  }
  
  // Pickup/Dropoff locations
  pdf.setFont('times', 'bold');
  pdf.text('Designated Pick-Up and Drop-Off Locations', leftMargin, y);
  y += 8;
  
  pdf.setFont('times', 'normal');
  pdf.text(`Primary Drop-Off Location: ${pickupDropoff.primaryDropOff || '________________________________'}`, leftMargin, y);
  y += 7;
  pdf.text(`Primary Pick-Up Location: ${pickupDropoff.primaryPickUp || '________________________________'}`, leftMargin, y);
  y += 7;
  pdf.text(`Secondary/Overflow Location (if applicable): ${pickupDropoff.secondaryLocation || '________________________________'}`, leftMargin, y);
  y += 12;
  
  // Emergency contact paragraph
  let emergencyPara = 'In case of emergencies or alerts please contact us on the above numbers.';
  if (notification.attachmentNote) {
    emergencyPara += ` ${notification.attachmentNote}`;
  }
  const emergencyLines = pdf.splitTextToSize(emergencyPara, pageWidth - 40);
  pdf.text(emergencyLines, leftMargin, y);
  y += emergencyLines.length * 6 + 15;
  
  // Closing
  pdf.text('Thank You', leftMargin, y);
  y += 20;
  
  // Signature line
  pdf.setDrawColor(0, 0, 0);
  pdf.line(leftMargin, y, leftMargin + 60, y);
  y += 5;
  
  if (signature.name) {
    pdf.setFont('times', 'bold');
    pdf.text(signature.name, leftMargin, y);
    y += 6;
  }
  if (signature.title) {
    pdf.setFont('times', 'normal');
    pdf.text(signature.title, leftMargin, y);
    y += 10;
  }
  
  // CC section
  const ccRecipients = (notification.ccRecipients || []).filter(r => r.name);
  if (ccRecipients.length > 0) {
    y += 5;
    pdf.setFont('times', 'bold');
    pdf.text('Cc:', leftMargin, y);
    y += 6;
    
    pdf.setFont('times', 'normal');
    ccRecipients.forEach(cc => {
      pdf.text(`${cc.name}${cc.address ? ` - ${cc.address}` : ''}`, leftMargin + 10, y);
      y += 5;
    });
  }
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`Transportation_Notification_${(notification.letterDate || 'Letter').replace(/\s+/g, '_')}.pdf`);
};

/**
 * Export Staff Assignment Report to PDF
 */
export const exportStaffAssignmentPDF = async (data, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(20, 184, 166); // Teal-500
  pdf.rect(0, y, pageWidth, 20, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('STAFF ASSIGNMENT REPORT', pageWidth / 2, y + 8, { align: 'center' });
  pdf.setFontSize(9);
  pdf.text(data.date || new Date().toLocaleDateString(), pageWidth / 2, y + 16, { align: 'center' });
  
  y += 27;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Staff Member: ${data.staffName || 'N/A'}`, 15, y);
  y += 8;
  pdf.text(`Classroom: ${data.classroom || 'N/A'}`, 15, y);
  y += 8;
  pdf.text(`Category: ${data.category || 'N/A'}`, 15, y);
  y += 12;
  
  // Assigned children
  if (data.assignedChildren && data.assignedChildren.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [['#', 'Child Name', 'Age/DOB', 'Notes']],
      body: data.assignedChildren.map((child, i) => [
        i + 1,
        child.name || '',
        child.age || child.birthdate || '',
        child.notes || ''
      ]),
      theme: 'striped',
      headStyles: { fillColor: [20, 184, 166], textColor: 255 },
      margin: { left: 15, right: 15 },
    });
    y = pdf.lastAutoTable.finalY + 10;
  }
  
  // Notes
  if (data.notes) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Notes:', 15, y);
    y += 6;
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(data.notes, pageWidth - 30);
    pdf.text(lines, 15, y);
  }
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`Staff_Assignment_${(data.staffName || 'Report').replace(/\s+/g, '_')}_${data.date || 'undated'}.pdf`);
};

/**
 * Export Sleeping Cots Assignment to PDF
 */
export const exportSleepingCotsPDF = async (data, centerName = '') => {
  const pdf = new jsPDF('landscape');
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(99, 102, 241); // Indigo-500
  pdf.rect(0, y, pageWidth, 15, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('SLEEPING COTS ASSIGNMENT', pageWidth / 2, y + 10, { align: 'center' });
  
  y += 20;
  
  // Cots table
  if (data.cots && data.cots.length > 0) {
    pdf.autoTable({
      startY: y,
      head: [['Cot #', 'Child Name', 'Nap Time', 'Special Instructions']],
      body: data.cots.map(cot => [
        cot.cotNumber || '',
        cot.childName || 'Unassigned',
        cot.napTime || '',
        cot.instructions || ''
      ]),
      theme: 'striped',
      headStyles: { fillColor: [99, 102, 241], textColor: 255 },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 60 },
        2: { cellWidth: 40 },
        3: { cellWidth: 'auto' }
      }
    });
  }
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 200, { align: 'center' });
  
  pdf.save(`Sleeping_Cots_${data.date || 'Report'}.pdf`);
};

/**
 * Export Parent Communication Log to PDF
 */
export const exportParentCommunicationPDF = async (data, centerName = '') => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add logo header with center name
  let y = await addBrandedHeader(pdf, 10, centerName);
  
  // Header
  pdf.setFillColor(249, 115, 22); // Orange-500
  pdf.rect(0, y, pageWidth, 15, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PARENT COMMUNICATION LOG', pageWidth / 2, y + 10, { align: 'center' });
  
  y += 22;
  
  pdf.setTextColor(0, 0, 0);
  
  // Communication details
  const details = [
    ['Child Name', data.childName || 'N/A'],
    ['Parent/Guardian', data.parentName || 'N/A'],
    ['Date', data.date || 'N/A'],
    ['Time', data.time || 'N/A'],
    ['Method', data.method || 'N/A'],
    ['Staff Member', data.staffName || 'N/A'],
  ];
  
  pdf.autoTable({
    startY: y,
    body: details,
    theme: 'plain',
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 130 }
    },
    margin: { left: 15, right: 15 },
  });
  y = pdf.lastAutoTable.finalY + 10;
  
  // Subject
  pdf.setFont('helvetica', 'bold');
  pdf.text('Subject:', 15, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.subject || 'N/A', 15, y);
  y += 10;
  
  // Message
  pdf.setFont('helvetica', 'bold');
  pdf.text('Message/Notes:', 15, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  const lines = pdf.splitTextToSize(data.message || 'N/A', pageWidth - 30);
  pdf.text(lines, 15, y);
  y += lines.length * 5 + 10;
  
  // Follow-up
  if (data.followUp) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Follow-up Required:', 15, y);
    y += 6;
    pdf.setFont('helvetica', 'normal');
    pdf.text(data.followUp, 15, y);
  }
  
  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  pdf.save(`Parent_Communication_${(data.childName || 'Log').replace(/\s+/g, '_')}_${data.date || 'undated'}.pdf`);
};

// Default export with all PDF functions
export default {
  exportElementToPDF,
  exportIncidentReportPDF,
  exportFireDrillPDF,
  exportStaffEvaluationPDF,
  exportMonthlyAttendancePDF,
  exportStaffRecordPDF,
  exportToExcel,
  exportAttendanceToExcel,
  exportDailyReportPDF,
  exportCribCardPDF,
  exportTransportationPDF,
  exportStaffAssignmentPDF,
  exportSleepingCotsPDF,
  exportParentCommunicationPDF
};
